import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package, Truck, MapPin, CheckCircle, ArrowRight, Phone, Lock } from 'lucide-react';

const WA_NUMBER = '27686129215';
const TEL_NUMBER = '0686129215';

const STEPS = [
  { icon: Package, label: 'Collected', sub: 'Cape Town pickup' },
  { icon: Truck, label: 'In Transit', sub: 'Cape Town → Gansbaai' },
  { icon: MapPin, label: 'At Depot', sub: 'Gansbaai depot' },
  { icon: CheckCircle, label: 'Delivered', sub: 'To your door' },
];

function WhatsAppIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function LandingPage() {
  const [tracking, setTracking] = useState('');
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('token');

  const handleTrack = (e) => {
    e.preventDefault();
    const t = tracking.trim().toUpperCase();
    if (!t) return;
    navigate(`/track/${t}`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
            <Truck size={16} className="text-white" />
          </div>
          <div>
            <span className="font-bold text-gray-900 text-lg leading-none block">Expressway</span>
            <span className="text-xs text-gray-400 leading-none">Courier</span>
          </div>
        </div>
        {isLoggedIn ? (
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            Go to Dashboard <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white text-xs font-bold tracking-widest uppercase px-5 py-2.5 rounded-lg transition-colors shadow-md"
          >
            <Lock size={13} /> Admin Portal
          </button>
        )}
      </header>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12 md:py-20">
        <div className="w-full max-w-xl text-center">
          <span className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
            <MapPin size={12} /> Cape Town → Gansbaai
          </span>

          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            Track Your Parcel
          </h1>
          <p className="text-gray-500 text-lg mb-10">
            Enter your tracking number to see real-time delivery updates.
          </p>

          {/* Tracking form */}
          <form onSubmit={handleTrack} className="flex gap-2 max-w-md mx-auto mb-4">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={tracking}
                onChange={(e) => setTracking(e.target.value.toUpperCase())}
                placeholder="e.g. EC2605ABC123"
                maxLength={20}
                autoFocus
                className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-200 rounded-xl text-sm font-mono uppercase focus:outline-none focus:border-orange-400 transition-colors shadow-sm"
              />
            </div>
            <button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3.5 rounded-xl transition-colors shadow-sm whitespace-nowrap"
            >
              Track
            </button>
          </form>
          <p className="text-xs text-gray-400">
            Your tracking number is printed on your collection slip.
          </p>
        </div>

        {/* Journey steps */}
        <div className="w-full max-w-2xl mt-16">
          <div className="grid grid-cols-4 gap-4">
            {STEPS.map((step, idx) => (
              <div key={step.label} className="flex flex-col items-center text-center gap-2">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-orange-50 border-2 border-orange-100 flex items-center justify-center">
                    <step.icon size={20} className="text-orange-500" />
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 left-full h-px bg-orange-100 -translate-y-1/2" style={{ width: 'calc(100% + 1rem)' }} />
                  )}
                </div>
                <p className="text-xs font-semibold text-gray-700">{step.label}</p>
                <p className="text-xs text-gray-400 leading-snug">{step.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-gray-50">
        <div className="max-w-2xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0">
              <Truck size={14} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-800 text-sm leading-none">Expressway Courier</p>
              <p className="text-xs text-gray-400">Cape Town → Gansbaai</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-sm">
            <div className="flex items-start gap-2">
              <MapPin size={15} className="text-orange-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-700 font-medium">56 Barnard Street</p>
                <p className="text-gray-400 text-xs">Gansbaai, 7220</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={`tel:${TEL_NUMBER}`}
                className="flex items-center gap-1.5 text-gray-700 font-medium hover:text-orange-500 transition-colors"
              >
                <Phone size={14} className="text-orange-400" />
                068 612 9215
              </a>
              <a
                href={`https://wa.me/${WA_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[#25D366] font-medium hover:opacity-80 transition-opacity"
              >
                <WhatsAppIcon size={16} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-100 py-3 text-center">
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} Expressway Courier
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp button */}
      <a
        href={`https://wa.me/${WA_NUMBER}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#20b558] text-white font-semibold text-sm px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all"
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon size={20} />
        <span className="hidden sm:inline">Chat with us</span>
      </a>
    </div>
  );
}
